import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Linkedin, Facebook, ArrowRight } from 'lucide-react';
import Container from '../common/Container';

// Navigation links data
const companyLinks = [
  { id: 'about', label: 'About Us', path: '/about' },
  { id: 'services', label: 'Services', path: '/services' },
  { id: 'projects', label: 'Projects', path: '/projects' },
  { id: 'careers', label: 'Careers', path: '/careers' },
  { id: 'blog', label: 'Blog', path: '/blog' },
    { id: 'innovation', label: 'Galo Innovation Lab', path: '/galoinnovationlab' },

  { id: 'contact', label: 'Contact', path: '/contact' }
];

const serviceLinks = [
  { id: 'engineering', label: 'Engineering & Design', path: '/services' },
  { id: 'software', label: 'Software Solutions', path: '/services' },
  { id: 'implementation', label: 'Implementation', path: '/services' },
  { id: 'innovative', label: 'Innovative Solutions', path: '/services' }
];

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white pt-20 pb-10 relative" role="contentinfo">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 opacity-30"></div>
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-br from-cyan-500/5 to-transparent rounded-bl-full"></div>
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tl from-blue-500/5 to-transparent rounded-tr-full"></div>
      
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          {/* Company Info */}
          <div className="md:col-span-5">
            <div className="mb-8">
              <Link to="/" aria-label="Go to homepage">
                <img 
                  src="/img/logo-white.png" 
                  alt="GALO Industries"
                  className="h-16 w-auto brightness-0 invert mb-6"
                  width="240"
                  height="64"
                />
              </Link>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Leading provider of industrial automation solutions. We transform manufacturing processes with innovative technology and engineering excellence.
              </p>
            </div>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-start group">
                <MapPin className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0 group-hover:text-cyan-300 transition-colors duration-300" aria-hidden="true" />
                <div className="group-hover:text-slate-300 transition-colors duration-300">
                  <p className="text-slate-300">Šibenska ulica 4B,<br/>23000, Zadar, Croatia</p>
                </div>
              </div>
              <div className="flex items-start group">
                <Mail className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0 group-hover:text-cyan-300 transition-colors duration-300" aria-hidden="true" />
                <div>
                  <a href="mailto:info@galo-industries.com" className="text-slate-300 hover:text-white transition-colors duration-300">
                    info@galo-industries.com
                  </a>
                </div>
              </div>
              <div className="flex items-start group">
                <Phone className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0 group-hover:text-cyan-300 transition-colors duration-300" aria-hidden="true" />
                <div>
                  <a href="tel:+385981991813" className="text-slate-300 hover:text-white transition-colors duration-300">
                    +385 98 199 1813
                  </a>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <a 
                href="https://linkedin.com/company/galo-industries" 
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-cyan-700 transition-all duration-300"
                aria-label="Follow us on LinkedIn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin className="w-5 h-5" aria-hidden="true" />
              </a>
              <a 
                href="https://facebook.com/galoindustries" 
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-cyan-700 transition-all duration-300"
                aria-label="Follow us on Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>
          </div>
          
          {/* Company Links */}
          <div className="md:col-span-2">
            <h3 className="text-white font-bold text-xl mb-6 flex items-center">
              <span className="w-1 h-6 bg-gradient-to-b from-cyan-400 to-blue-500 mr-2 rounded-full inline-block"></span>
              Company
            </h3>
            <ul className="space-y-4">
              {companyLinks.map(link => (
                <li key={link.id}>
                  <Link 
                    to={link.path} 
                    className="text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Services Links */}
          <div className="md:col-span-2">
            <h3 className="text-white font-bold text-xl mb-6 flex items-center">
              <span className="w-1 h-6 bg-gradient-to-b from-cyan-400 to-blue-500 mr-2 rounded-full inline-block"></span>
              Services
            </h3>
            <ul className="space-y-4">
              {serviceLinks.map(link => (
                <li key={link.id}>
                  <Link 
                    to={link.path} 
                    className="text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Newsletter */}
          <div className="md:col-span-3">
            <h3 className="text-white font-bold text-xl mb-6 flex items-center">
              <span className="w-1 h-6 bg-gradient-to-b from-cyan-400 to-blue-500 mr-2 rounded-full inline-block"></span>
              Newsletter
            </h3>
            <p className="text-slate-400 mb-4">Stay updated with our latest news and projects</p>
            
            <form className="mb-4" onSubmit={(e) => e.preventDefault()}>
              <div className="flex">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  aria-label="Email address for newsletter"
                  className="flex-1 px-4 py-3 rounded-l-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <button 
                  type="submit"
                  className="px-4 py-3 bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-500 hover:to-blue-600 text-white rounded-r-lg transition-colors duration-300 flex items-center"
                  aria-label="Subscribe to newsletter"
                >
                  Subscribe
                  <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              </div>
            </form>
            
            <p className="text-slate-500 text-sm flex items-start">
              <svg className="w-4 h-4 mr-2 mt-0.5 text-cyan-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              By subscribing, you agree to our Privacy Policy
            </p>
          </div>
        </div>
        
        {/* Footer Bottom */}
        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between">
          <p className="text-slate-500 text-sm mb-6 md:mb-0">
            © {currentYear} Galo Industries. All rights reserved.
          </p>
          <div className="flex space-x-6 text-sm">
            <Link to="/privacy-policy" className="text-slate-500 hover:text-white transition-colors duration-300">Privacy Policy</Link>
            <Link to="/terms" className="text-slate-500 hover:text-white transition-colors duration-300">Terms of Service</Link>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
